import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { LeadCaptureModal } from "@/components/LeadCaptureModal";
import {
  Search, Zap, ExternalLink, TrendingUp, RotateCcw, Building2, Wrench,
  ChevronDown, Globe, Loader2, ShieldCheck, BrainCircuit,
  Lightbulb, Users, FileText, Mail, Send, ArrowRight, Clock, DollarSign,
  BarChart3, Cpu, Lock, Eye, UserCheck, Layers, Stethoscope, Briefcase,
  Home as HomeIcon, ShoppingBag, Target, Star, Quote, Rocket,
} from "lucide-react";
import { ReportView } from "@/components/ReportView";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = [
  { value: "Dentist", label: "Dentist" },
  { value: "Real Estate", label: "Real Estate" },
  { value: "Agency", label: "Agency" },
  { value: "Consulting", label: "Consulting" },
  { value: "Retail", label: "Retail" },
  { value: "Other", label: "Other (type below)" },
];

const PAIN_POINTS = [
  "Scheduling", "Missed Calls", "Organization", "Lead Management",
  "Communication", "Inventory", "Payment Processing", "Sales Tracking",
  "Automation", "Content Creation", "Project Management", "Patient Communication",
  "Other"
];

/* =========================================================
   INLINE HERO — single viewport center content
   ========================================================= */
function HeroCenter({ onAnalyze, loading, loadingPhase }) {
  const [industry, setIndustry] = useState("");
  const [customIndustry, setCustomIndustry] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [customPainPoint, setCustomPainPoint] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [location, setLocation] = useState("");
  const [step, setStep] = useState(1); // 1 = url input, 2 = details
  const [error, setError] = useState(null);
  const [hoveredLetter, setHoveredLetter] = useState(null); // index of hovered char

  const resolvedIndustry = industry === "Other" ? customIndustry.trim() : industry;
  const resolvedPainPoint = painPoint === "Other" ? customPainPoint.trim() : painPoint;

  const handleUrlNext = () => {
    setStep(2);
  };

  const handleAnalyze = () => {
    if (!industry) { toast.error("Select an industry first"); return; }
    if (industry === "Other" && !customIndustry.trim()) { toast.error("Please describe your industry"); return; }
    if (!painPoint) { toast.error("Select a pain point"); return; }
    if (painPoint === "Other" && !customPainPoint.trim()) { toast.error("Please describe your pain point"); return; }
    setError(null);
    onAnalyze({ industry: resolvedIndustry, painPoint: resolvedPainPoint, teamSize, websiteUrl, location });
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-6">
      {step === 1 ? (
        /* ── Step 1: URL + headline ── */
        <div className="text-center animate-fade-in-up opacity-0" style={{ animationFillMode: "forwards" }}>
          {/* Eyebrow — Kortix metallic shimmer */}
          <p className="animate-shiny-text text-xs uppercase tracking-[0.2em] font-medium mb-6">
            AI Automation Consultant
          </p>

          {/* H1 — text is a readable string; spans only exist for the per-letter hover effect */}
          <h1
            data-testid="hero-title"
            aria-label="Stop guessing tools. Get a clear AI automation blueprint for small businesses."
            className="text-4xl sm:text-5xl font-light text-white tracking-tight leading-[1.15] mb-4 max-w-3xl mx-auto cursor-default select-none"
          >
            {"Stop guessing tools. Get a clear AI automation blueprint."
              .split("")
              .map((char, i) => {
                const isHovered = hoveredLetter === i;
                const isSpace = char === " ";
                return (
                  <span
                    key={i}
                    aria-hidden="true"
                    onMouseEnter={() => !isSpace && setHoveredLetter(i)}
                    onMouseLeave={() => setHoveredLetter(null)}
                    style={{
                      display: "inline-block",
                      transition: "transform 0.35s cubic-bezier(0.34,1.56,0.64,1), color 0.2s ease",
                      transform: isHovered
                        ? "translateY(-7px) skewX(-6deg) scale(1.08)"
                        : "translateY(0) skewX(0deg) scale(1)",
                      color: isHovered
                        ? "rgba(255,255,255,0.95)"
                        : i < 21
                          ? "rgba(255,255,255,0.9)"
                          : "rgba(161,161,170,0.75)",
                      willChange: "transform",
                    }}
                  >
                    {isSpace ? "\u00A0" : char}
                  </span>
                );
              })}
          </h1>

          <p
            data-testid="hero-subtitle"
            className="text-sm font-light text-zinc-500 max-w-lg mx-auto mb-10 leading-relaxed"
          >
            Enter your website — get a personalized AI stack in 60 seconds. Free, no login required.
          </p>

          {/* Input row */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-xl mx-auto mb-8">
            <input
              data-testid="website-url-input"
              type="text"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleUrlNext()}
              placeholder="yourwebsite.com"
              className="flat-input w-full h-11 px-4 text-sm font-normal"
            />
            <button
              data-testid="hero-cta-button"
              onClick={handleUrlNext}
              className="flat-btn-primary shrink-0 h-11 px-6 font-medium text-sm"
            >
              Get Blueprint
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Trust micro-badges */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {[
              { icon: ShieldCheck, text: "Free Analysis" },
              { icon: Clock, text: "60-Second Results" },
              { icon: Lock, text: "No Login Required" },
            ].map((b, i) => (
              <div key={i} className="info-pill">
                <b.icon className="w-3 h-3" />
                <span className="font-normal">{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* ── Step 2: details panel ── */
        <div
          id="wizard"
          data-testid="wizard-section"
          className="animate-scale-in opacity-0"
          style={{ animationFillMode: "forwards" }}
        >
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-widest text-zinc-500 font-medium mb-2">
              Configure Analysis
            </p>
            <h2 data-testid="wizard-title" className="text-2xl font-light text-white tracking-tight">
              Tell us about your business
            </h2>
            {websiteUrl && (
              <p className="text-xs text-zinc-500 mt-1">
                Analysing <span className="text-zinc-300">{websiteUrl}</span>
              </p>
            )}
          </div>

          <div className="matte-card p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Industry */}
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-zinc-400 uppercase tracking-wider mb-3">
                  <Building2 className="w-3.5 h-3.5" />
                  Your Industry
                </label>
                <Select value={industry} onValueChange={(v) => { setIndustry(v); if (v !== "Other") setCustomIndustry(""); }}>
                  <SelectTrigger
                    data-testid="industry-dropdown"
                    className="w-full h-11 flat-input rounded-lg text-white text-sm px-4 border-0 focus:ring-0"
                  >
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border border-white/10 rounded-lg">
                    {INDUSTRIES.map((ind) => (
                      <SelectItem
                        key={ind.value}
                        value={ind.value}
                        data-testid={`industry-option-${ind.value.toLowerCase().replace(/\s+/g, "-")}`}
                        className="text-white hover:bg-white/5 focus:bg-white/5 text-sm rounded cursor-pointer"
                      >
                        {ind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {industry === "Other" && (
                  <input
                    type="text"
                    value={customIndustry}
                    onChange={(e) => setCustomIndustry(e.target.value)}
                    placeholder="e.g. Childcare, Gym, Funeral Services…"
                    className="flat-input w-full h-10 px-4 text-sm mt-2"
                    autoFocus
                  />
                )}
              </div>

              {/* Team Size */}
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-zinc-400 uppercase tracking-wider mb-3">
                  <Users className="w-3.5 h-3.5" />
                  Team Size
                </label>
                <input
                  data-testid="team-size-input"
                  type="number"
                  min="1"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  placeholder="e.g. 10"
                  className="flat-input w-full h-11 px-4 text-sm"
                />
              </div>

              {/* Location */}
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-zinc-400 uppercase tracking-wider mb-3">
                  <Globe className="w-3.5 h-3.5" />
                  Location
                </label>
                <input
                  data-testid="location-input"
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. Melbourne, Sydney..."
                  className="flat-input w-full h-11 px-4 text-sm"
                />
              </div>
            </div>

            {/* Pain Points */}
            <div className="mb-8">
              <label className="flex items-center gap-2 text-xs font-medium text-zinc-400 uppercase tracking-wider mb-3">
                <Wrench className="w-3.5 h-3.5" />
                Biggest Pain Point
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {PAIN_POINTS.map((pp) => (
                  <button
                    key={pp}
                    data-testid={`pain-point-${pp.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => { setPainPoint(pp); if (pp !== "Other") setCustomPainPoint(""); }}
                    className={`text-left px-3 py-2.5 rounded-lg text-xs font-medium border transition-all duration-150 cursor-pointer ${painPoint === pp
                      ? "pain-btn-active border-white/25 bg-white/6 text-white"
                      : "border-white/7 bg-white/2 text-zinc-400 hover:border-white/15 hover:text-zinc-200"
                      }`}
                  >
                    {pp}
                  </button>
                ))}
              </div>
              {painPoint === "Other" && (
                <input
                  type="text"
                  value={customPainPoint}
                  onChange={(e) => setCustomPainPoint(e.target.value)}
                  placeholder="e.g. Too much manual data entry…"
                  className="flat-input w-full h-10 px-4 text-sm mt-3"
                  autoFocus
                />
              )}
            </div>

            {/* Error */}
            {error && (
              <div
                data-testid="wizard-error"
                className="mb-6 px-4 py-3 rounded-lg bg-red-950/30 border border-red-500/20 text-red-400 text-xs"
              >
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={() => setStep(1)}
                className="flat-btn-ghost text-xs"
              >
                ← Back
              </button>
              <button
                data-testid="analyze-button"
                onClick={handleAnalyze}
                disabled={loading}
                className="flat-btn-primary disabled:opacity-40 disabled:cursor-not-allowed min-w-[180px] justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {loadingPhase || "Analyzing…"}
                  </>
                ) : (
                  <>
                    <Search className="w-3.5 h-3.5" />
                    Analyze My Business →
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================
   RESULT CARD
   ========================================================= */
function ResultCard({ tool, index }) {
  const [showSteps, setShowSteps] = useState(false);

  return (
    <div
      data-testid={`result-card-${tool.id}`}
      className="matte-card group rounded-xl opacity-0 animate-fade-in-up overflow-hidden hover:border-white/15 transition-colors duration-200"
      style={{ animationDelay: `${index * 80}ms`, animationFillMode: "forwards" }}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <h3 data-testid={`tool-name-${tool.id}`} className="text-base font-semibold text-white">
            {tool.name}
          </h3>
          <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/7 flex items-center justify-center flex-shrink-0">
            <Zap className="w-4 h-4 text-zinc-400" />
          </div>
        </div>

        <p data-testid={`tool-description-${tool.id}`} className="text-xs text-zinc-400 leading-relaxed mb-3">
          {tool.description}
        </p>

        {tool.why_it_fits && (
          <div
            data-testid={`tool-why-${tool.id}`}
            className="flex items-start gap-2 mb-2.5 px-3 py-2 rounded-lg bg-white/3 border border-white/6"
          >
            <Lightbulb className="w-3 h-3 text-zinc-400 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-zinc-400 leading-relaxed">{tool.why_it_fits}</span>
          </div>
        )}

        {tool.recommendation_reason && (
          <div
            data-testid={`tool-reason-${tool.id}`}
            className="flex items-start gap-2 mb-2.5 px-3 py-2 rounded-lg bg-white/3 border border-white/6"
          >
            <Lightbulb className="w-3 h-3 text-zinc-500 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-zinc-500 leading-relaxed">{tool.recommendation_reason}</span>
          </div>
        )}

        <div
          data-testid={`tool-roi-${tool.id}`}
          className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-white/3 border border-white/6"
        >
          <TrendingUp className="w-3 h-3 text-zinc-400 flex-shrink-0" />
          <div>
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 block">ROI Impact</span>
            <span className="text-xs text-zinc-300">{tool.roi_text}</span>
          </div>
        </div>

        {tool.implementation_steps && tool.implementation_steps.length > 0 && (
          <div className="mb-3">
            <button
              onClick={() => setShowSteps(!showSteps)}
              className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 transition-colors duration-150 text-xs font-medium mb-2"
            >
              <Layers className="w-3 h-3" />
              Implementation Steps
              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showSteps ? "rotate-180" : ""}`} />
            </button>
            {showSteps && (
              <div className="space-y-2 pl-1 animate-fade-in opacity-0" style={{ animationFillMode: "forwards" }}>
                {tool.implementation_steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-[10px] text-zinc-600 font-bold mt-0.5 w-4 flex-shrink-0">{i + 1}.</span>
                    <span className="text-xs text-zinc-500 leading-relaxed">{step}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <a
          data-testid={`affiliate-link-${tool.id}`}
          href={`${API}/redirect/${tool.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flat-btn-dark w-full justify-center text-xs"
          style={{ borderRadius: "8px" }}
        >
          Get Started
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

/* =========================================================
   MARKDOWN REPORT
   ========================================================= */
function MarkdownReport({ reportMarkdown }) {
  const [isOpen, setIsOpen] = useState(false);
  if (!reportMarkdown) return null;

  return (
    <div data-testid="markdown-report-section" className="mt-8 animate-fade-in opacity-0" style={{ animationFillMode: "forwards", animationDelay: "300ms" }}>
      <button
        data-testid="toggle-report-button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-lg matte-card hover:border-white/15 transition-colors duration-150"
      >
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-zinc-400" />
          <span className="text-sm font-medium text-white">Full AI Stack Report</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div
          data-testid="markdown-report-content"
          className="mt-2 p-5 rounded-lg matte-card text-xs text-zinc-400 leading-relaxed whitespace-pre-wrap overflow-x-auto"
        >
          {reportMarkdown}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   RESULTS SECTION
   ========================================================= */
function ResultsSection({ results, onReset, resultsRef, onOpenLeadModal }) {
  if (!results) return null;
  const { tools, scannedSummary, analysisSummary, recommendationReason, confidenceScore, reportMarkdown, traffic_data } = results;

  return (
    <section ref={resultsRef} data-testid="results-section" className="py-16">
      <div className="max-w-5xl mx-auto px-6">
        {/* Analysis Banner */}
        <div
          data-testid="analysis-banner"
          className="matte-card rounded-xl p-5 mb-8 animate-fade-in opacity-0"
          style={{ animationFillMode: "forwards" }}
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <BrainCircuit className="w-4 h-4 text-zinc-400" />
                <span className="text-sm font-semibold text-white">AI Analysis Complete</span>
              </div>
              {analysisSummary && (
                <p data-testid="analysis-summary" className="text-xs text-zinc-400 leading-relaxed mb-1">
                  {analysisSummary}
                </p>
              )}
              {recommendationReason && (
                <p data-testid="recommendation-reason" className="text-xs text-zinc-500">
                  {recommendationReason}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              {confidenceScore > 0 && (
                <div data-testid="confidence-score" className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500 uppercase tracking-wider">Confidence</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 rounded-full bg-white/6 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{
                          width: `${confidenceScore}%`,
                          background: confidenceScore >= 75 ? "#22C55E" : confidenceScore >= 50 ? "#a1a1aa" : "#F59E0B",
                        }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-white">{confidenceScore}%</span>
                  </div>
                </div>
              )}
              {scannedSummary && (
                <div data-testid="scanned-summary-badge" className="info-pill">
                  <ShieldCheck className="w-3 h-3 text-zinc-400" />
                  <span className="text-zinc-400">{scannedSummary}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Results heading */}
        <div className="mb-8 animate-fade-in opacity-0" style={{ animationFillMode: "forwards", animationDelay: "80ms" }}>
          <h2 data-testid="results-title" className="text-2xl font-semibold text-white mb-1">
            {tools.length > 0 ? "Your Custom AI Stack" : "No Matches Found"}
          </h2>
          <p className="text-sm text-zinc-500">
            {tools.length > 0
              ? "Ranked by relevance to your industry, team size, and pain point."
              : "Try a different combination of industry and pain point."}
          </p>
        </div>

        {tools.length > 0 && (
          <div data-testid="results-grid" className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-4">
            {tools.map((tool, i) => <ResultCard key={tool.id} tool={tool} index={i} />)}
          </div>
        )}

        <MarkdownReport reportMarkdown={reportMarkdown} />

        {/* Lead capture */}
        {tools.length > 0 && (
          <div className="mt-8 animate-fade-in opacity-0" style={{ animationFillMode: "forwards", animationDelay: "400ms" }}>
            <div className="matte-card rounded-xl p-6 text-center max-w-md mx-auto">
              <Mail className="w-6 h-6 text-zinc-400 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-white mb-1">Want the full report?</h3>
              <p className="text-xs text-zinc-500 mb-5 max-w-xs mx-auto">
                Get a detailed AI automation report with ROI projections sent to your inbox.
              </p>
              <button
                data-testid="get-report-button"
                onClick={onOpenLeadModal}
                className="flat-btn-primary mx-auto"
              >
                <Send className="w-3.5 h-3.5" />
                Send My Report →
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-center mt-8">
          <button
            data-testid="reset-button"
            onClick={onReset}
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-300 transition-colors duration-150 text-xs"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Start Over
          </button>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   HOME PAGE — single viewport (h-screen) shell
   ========================================================= */
export default function HomePage() {
  const [results, setResults] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState("");
  const [industry, setIndustryState] = useState("");
  const [websiteUrl, setWebsiteUrlState] = useState("");
  const resultsRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#wizard") {
      // no-op: hero center already shows wizard inline
    }
  }, [location]);

  const handleAnalyze = useCallback(async ({ industry, painPoint, teamSize, websiteUrl, location: loc }) => {
    setIndustryState(industry);
    setWebsiteUrlState(websiteUrl);
    setLoading(true);

    const phases = websiteUrl.trim()
      ? ["Analyzing your business…", "Scanning your website…", "Generating recommendations…"]
      : ["Analyzing your business…", "Generating recommendations…"];

    let phaseIdx = 0;
    setLoadingPhase(phases[0]);
    const interval = setInterval(() => {
      phaseIdx++;
      if (phaseIdx < phases.length) setLoadingPhase(phases[phaseIdx]);
    }, 1200);

    try {
      const payload = { industry, painPoint };
      if (teamSize && parseInt(teamSize) > 0) payload.teamSize = parseInt(teamSize);
      if (websiteUrl.trim()) payload.website_url = websiteUrl.trim();
      if (loc && loc.trim()) payload.location = loc.trim();

      const response = await axios.post(`${API}/recommend`, payload);
      const data = response.data;

      setResults({
        tools: data.tools,
        scannedSummary: data.scanned_summary || null,
        analysisSummary: data.analysis_summary || null,
        recommendationReason: data.recommendation_reason || null,
        confidenceScore: data.confidence_score || 0,
        reportMarkdown: data.reportMarkdown || null,
        blueprint: data.blueprint || null,
        traffic_data: data.traffic_data || null,
        scanned_domain: data.scanned_domain || null,
        scanned_title: data.scanned_title || null,
        detected_tools: data.detected_tools || [],
        industry_signals: data.industry_signals || [],
        pages_scanned: data.pages_scanned || [],
        scan_gaps: data.scan_gaps || [],
        tech_debt_flags: data.tech_debt_flags || [],
        yami_breakdown: data.yami_breakdown || null,
        roi_benchmarks: data.roi_benchmarks || [],
        teamSize: teamSize ? parseInt(teamSize) : null,
        painPoint: painPoint || null,
        industry: industry,
        location: data.location || loc || null,
        capturedEmail: "",
      });

      if (data.tools.length === 0) {
        toast.info("No matching tools found. Try different selections.");
      } else {
        toast.success(`Found ${data.tools.length} tool${data.tools.length > 1 ? "s" : ""} for you`);
      }

      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      console.error(err);
      toast.error("Failed to get recommendations. Please try again.");
    } finally {
      clearInterval(interval);
      setLoading(false);
      setLoadingPhase("");
    }
  }, []);

  const handleReset = useCallback(() => {
    setResults(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className={`bg-[#09090b] ${results ? "min-h-screen" : "h-screen overflow-hidden"}`}>
      <Helmet>
        <title>Yukti — Free AI Automation Blueprint for Small Businesses</title>
        <meta name="description" content="Yukti scans your business website and delivers a free AI automation blueprint in 60 seconds. Discover the best workflow automation tools, AI stack, and ROI projections for your industry." />
      </Helmet>

      <div className={`max-w-6xl mx-auto flex flex-col ${results ? "min-h-screen" : "h-screen"}`}>
        <Navbar />

        {!results && (
          <main className="flex-1 flex items-center justify-center pt-14">
            <HeroCenter
              onAnalyze={handleAnalyze}
              loading={loading}
              loadingPhase={loadingPhase}
            />
          </main>
        )}

        {results && (
          <main className="flex-1 pt-14">
            <div ref={resultsRef}>
              <ReportView results={results} onReset={handleReset} />
            </div>
            <ResultsSection
              results={null}
              onReset={handleReset}
              resultsRef={resultsRef}
              onOpenLeadModal={() => setShowLeadModal(true)}
            />
          </main>
        )}

        <Footer />
      </div>

      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        websiteUrl={websiteUrl}
        industry={industry}
        recommendedTools={results?.tools}
      />
    </div>
  );
}

